import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, ArrowRight, Workflow as WorkflowIcon, Save, Play, Trash2, Sparkles } from 'lucide-react';
import { TOOLS } from '@/registry';
import { CATEGORY_BY_ID } from '@/registry/categories';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { storage } from '@/lib/storage';
import { uuid } from '@/lib/utils';
import { cn } from '@/lib/cn';
import { toast } from 'sonner';

interface SavedWorkflow {
  id: string;
  name: string;
  steps: string[];
  createdAt: number;
}

const TEMPLATES: Array<{ name: string; steps: string[]; description: string }> = [
  {
    name: 'OCR → table → CSV',
    steps: ['ocr-extract', 'extract-tables', 'csv-clean'],
    description: 'Pull text from a scan, extract tables, then clean the data',
  },
  {
    name: 'Image → social pack',
    steps: ['image-resize', 'image-compress', 'image-watermark'],
    description: 'Resize for socials, compress, and watermark in one go',
  },
  {
    name: 'PDF → editable Word',
    steps: ['pdf-to-images', 'ocr-extract', 'markdown-preview'],
    description: 'Render PDF, OCR the text, prepare an editable doc',
  },
  {
    name: 'CSV cleanup pipeline',
    steps: ['csv-clean', 'csv-dedupe', 'csv-to-json'],
    description: 'Clean whitespace, drop duplicates, output JSON',
  },
];

export function WorkflowsView() {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<SavedWorkflow[]>(() =>
    storage.get<SavedWorkflow[]>('workflows', []),
  );
  const [building, setBuilding] = useState<string[] | null>(null);
  const [name, setName] = useState('');
  const [picker, setPicker] = useState(false);
  const [filter, setFilter] = useState('');

  const filteredTools = useMemo(() => {
    const q = filter.toLowerCase();
    if (!q) return TOOLS.slice(0, 24);
    return TOOLS.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.keywords.some((k) => k.includes(q)) ||
        t.description.toLowerCase().includes(q),
    ).slice(0, 24);
  }, [filter]);

  function startNew(steps: string[] = []) {
    setBuilding(steps);
    setName('');
  }

  function addStep(toolId: string) {
    if (!building) return;
    setBuilding([...building, toolId]);
    setPicker(false);
    setFilter('');
  }

  function removeStep(i: number) {
    if (!building) return;
    setBuilding(building.filter((_, idx) => idx !== i));
  }

  function save() {
    if (!building || building.length === 0) return;
    const wf: SavedWorkflow = {
      id: uuid(),
      name: name || `Workflow ${workflows.length + 1}`,
      steps: building,
      createdAt: Date.now(),
    };
    const next = [wf, ...workflows];
    setWorkflows(next);
    storage.set('workflows', next);
    setBuilding(null);
    toast.success('Workflow saved');
  }

  function deleteWf(id: string) {
    const next = workflows.filter((w) => w.id !== id);
    setWorkflows(next);
    storage.set('workflows', next);
    toast.success('Workflow deleted');
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="flex items-center gap-2">
            <WorkflowIcon size={14} className="text-fg-subtle" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-fg-subtle">
              Workflows
            </h2>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">
            Chain utilities into a pipeline
          </h1>
          <p className="text-sm text-fg-muted mt-2 max-w-xl">
            Compose any tool into a multi-step automation. Save it, run it, share it with your team.
          </p>
        </div>
        {!building && (
          <Button variant="primary" onClick={() => startNew()}>
            <Plus size={14} />
            New workflow
          </Button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {building !== null ? (
          <motion.div
            key="builder"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            <Card>
              <div className="flex items-center justify-between mb-5">
                <Input
                  placeholder="Name your workflow…"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="max-w-xs"
                />
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setBuilding(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={save}
                    disabled={building.length === 0}
                  >
                    <Save size={12} />
                    Save
                  </Button>
                </div>
              </div>

              {/* Steps */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {building.map((toolId, i) => {
                  const t = TOOLS.find((x) => x.id === toolId);
                  if (!t) return null;
                  const cat = CATEGORY_BY_ID[t.category];
                  const Icon = t.icon;
                  return (
                    <div key={`${toolId}-${i}`} className="flex items-center gap-1">
                      <div className="group relative inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg bg-bg-inset border border-border">
                        <span
                          className={cn(
                            'size-5 rounded flex items-center justify-center bg-gradient-to-br',
                            cat?.gradient,
                          )}
                        >
                          <Icon size={11} className="text-white" />
                        </span>
                        <span className="text-xs font-medium">{t.name}</span>
                        <button
                          onClick={() => removeStep(i)}
                          className="opacity-0 group-hover:opacity-100 transition text-fg-subtle hover:text-danger"
                        >
                          <X size={11} />
                        </button>
                      </div>
                      {i < building.length - 1 && (
                        <ArrowRight size={12} className="text-fg-subtle" />
                      )}
                    </div>
                  );
                })}
                {building.length > 0 && <ArrowRight size={12} className="text-fg-subtle" />}
                <button
                  onClick={() => setPicker(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-border hover:border-accent hover:bg-accent/5 transition text-xs"
                >
                  <Plus size={12} />
                  Add step
                </button>
              </div>

              {building.length === 0 && (
                <div className="text-xs text-fg-subtle py-6 text-center border-t border-border">
                  Click <strong>Add step</strong> to start composing your pipeline
                </div>
              )}
            </Card>

            {/* Picker */}
            <AnimatePresence>
              {picker && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="mt-3"
                >
                  <Card>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold">Pick a tool</h3>
                      <button
                        onClick={() => {
                          setPicker(false);
                          setFilter('');
                        }}
                        className="text-fg-subtle hover:text-fg"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <Input
                      placeholder="Search tools…"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      className="mb-3"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-96 overflow-y-auto pr-1">
                      {filteredTools.map((t) => {
                        const cat = CATEGORY_BY_ID[t.category];
                        const Icon = t.icon;
                        return (
                          <button
                            key={t.id}
                            onClick={() => addStep(t.id)}
                            className="flex items-center gap-2.5 p-2.5 rounded-lg border border-border hover:border-accent/40 hover:bg-accent/5 text-left transition"
                          >
                            <span
                              className={cn(
                                'size-7 rounded flex items-center justify-center bg-gradient-to-br shrink-0',
                                cat?.gradient,
                              )}
                            >
                              <Icon size={12} className="text-white" />
                            </span>
                            <div className="min-w-0">
                              <div className="text-xs font-medium truncate">{t.name}</div>
                              <div className="text-2xs text-fg-subtle truncate">
                                {cat?.name}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Templates */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={12} className="text-orange-500 dark:text-amber-400" />
                <h2 className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">
                  Templates
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {TEMPLATES.map((t, i) => (
                  <Card key={i} hover onClick={() => startNew(t.steps)}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold">{t.name}</h3>
                      <Badge variant="accent">template</Badge>
                    </div>
                    <p className="text-xs text-fg-muted leading-relaxed mb-3">
                      {t.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-1">
                      {t.steps.map((id, j) => {
                        const tool = TOOLS.find((x) => x.id === id);
                        return (
                          <div key={`${id}-${j}`} className="flex items-center gap-1">
                            <span className="text-2xs px-2 py-0.5 rounded bg-bg-inset border border-border font-mono">
                              {tool?.name ?? id}
                            </span>
                            {j < t.steps.length - 1 && (
                              <ArrowRight size={10} className="text-fg-subtle" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                ))}
              </div>
            </section>

            {/* Saved */}
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-fg-subtle mb-3">
                Saved workflows
              </h2>
              {workflows.length === 0 ? (
                <EmptyState
                  icon={<WorkflowIcon size={20} />}
                  title="No saved workflows yet"
                  description="Build a multi-step pipeline using your favorite tools."
                  action={
                    <Button variant="primary" onClick={() => startNew()}>
                      <Plus size={14} />
                      New workflow
                    </Button>
                  }
                />
              ) : (
                <div className="space-y-2">
                  {workflows.map((wf) => (
                    <Card key={wf.id}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold mb-2">{wf.name}</h3>
                          <div className="flex flex-wrap items-center gap-1">
                            {wf.steps.map((id, i) => {
                              const tool = TOOLS.find((x) => x.id === id);
                              return (
                                <div key={`${id}-${i}`} className="flex items-center gap-1">
                                  <span className="text-2xs px-2 py-0.5 rounded bg-bg-inset border border-border font-mono">
                                    {tool?.name ?? id}
                                  </span>
                                  {i < wf.steps.length - 1 && (
                                    <ArrowRight size={10} className="text-fg-subtle" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-4">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => navigate(`/tool/${wf.steps[0]}`)}
                          >
                            <Play size={11} />
                            Run
                          </Button>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            onClick={() => deleteWf(wf.id)}
                          >
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </section>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
